import React, { useEffect, useState } from 'react';
import Select, { components, Creatable } from 'react-select';
import { Button, Card, CardBody, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Colxx } from '../../../../components/common/CustomBootstrap';
import { firestore } from '../../../../helpers/Firebase';
import { useSelector } from 'react-redux';
import { accessTypes } from '../../../../helpers/Utils';

const CustomSelectInput = props => {
    var customProps = Object.assign({}, props);
    delete customProps.autoCorrect;
    delete customProps.autoCapitalize;
    return <components.Input {...customProps} />;

};

const Variation = ({ selectedWasher, selectedVariation, service, addVariation, editVariation, dismiss, carKey, carType, availableVariation, addAvailableVariation }) => {
    // Get authUser to check admin access
    const { user } = useSelector(state => state.authUser);
    const isAdmin = accessTypes.includes(user?.accessType) || user?.accessType === 'superAdmin';
    
    // Detect if this is a PPF service
    const isPPFService = service?.type?.toLowerCase() === 'ppf';
    const legacyWindowTintSideKeys = ['frontSidePrice', 'sidesBackPrice', 'roofTintingPrice', 'allSidePrice'];

    // Initialize data based on service type
    const getInitialData = () => {
        if (isPPFService) {
            // For PPF, brand and madeIn are in general details, warranties (with pricing), finish, modelSeries and sides pricing in options table
            // Finish is stored as object with key, name, and name_ar (similar to tintingSides)
            // Warranties is an object where keys are warranty values and values contain pricing for each car type
            // TintingSides is an object where keys are side keys and values contain pricing for each car type
            // ModelSeries is stored as object with name and name_ar
            return [{ warranties: {}, tintingSides: {}, finish: null, modelSeries: { name: '', name_ar: '' } }]; // Warranties (with pricing), tintingSides (with pricing), finish, modelSeries in options table
        }
        return [{ heatRejection: '', darknessLevels: [], tintingSides: {} }];
    };

    const [variation, setVariation] = useState({})
    const [data, setData] = useState(getInitialData())

    useEffect(() => {
        if (editVariation) {
            if (isPPFService) {
                // For PPF, just set the data as is (no sorting needed)
                // Convert old warranty format to new warranties format if needed
                // Convert old side pricing format to new tintingSides format if needed (for UI only, will be flattened on save)
                const convertedData = selectedVariation?.data?.map(item => {
                    let converted = { ...item };

                    // Convert old single warranty to new warranties object format
                    if (item.warranty && !item.warranties) {
                        converted.warranties = { [item.warranty]: { [carKey]: '' } };
                        delete converted.warranty;
                    }
                    if (!converted.warranties) {
                        converted.warranties = {};
                    }

                    // Convert old flattened side pricing format to new tintingSides format (for UI)
                    // This is only for the UI - we'll flatten it back when saving
                    if (!converted.tintingSides && service?.tintingSides) {
                        converted.tintingSides = {};
                        service.tintingSides.forEach(side => {
                            // Check if side exists in flattened format (item[side.key])
                            if (item[side.key] && typeof item[side.key] === 'object') {
                                // Convert from flattened format: item[side.key] to tintingSides[side.key]
                                // Preserve all car keys from the flattened format
                                converted.tintingSides[side.key] = { ...item[side.key] };
                            }
                        });
                    }
                    if (!converted.tintingSides) {
                        converted.tintingSides = {};
                    }

                    // Remove flattened side properties to avoid duplication (they're now in tintingSides)
                    if (service?.tintingSides) {
                        service.tintingSides.forEach(side => {
                            if (converted.tintingSides[side.key] && converted[side.key]) {
                                delete converted[side.key];
                            }
                        });
                    }

                    return converted;
                }) || [{ warranties: {}, tintingSides: {}, finish: null, modelSeries: { name: '', name_ar: '' } }];
                setData(convertedData);
            } else {
                // For window tint, normalize both legacy flat side keys and tintingSides schema.
                const convertedData = selectedVariation?.data?.map(item => {
                    const converted = { ...item };
                    const serviceSideKeys = (service?.tintingSides || []).map(side => side?.key).filter(Boolean);
                    const candidateSideKeys = [...new Set([...serviceSideKeys, ...legacyWindowTintSideKeys])];

                    converted.tintingSides = converted.tintingSides && typeof converted.tintingSides === 'object'
                        ? { ...converted.tintingSides }
                        : {};

                    // Mirror legacy flat keys into tintingSides for validation/new schema reads.
                    candidateSideKeys.forEach(sideKey => {
                        if (converted[sideKey] && typeof converted[sideKey] === 'object') {
                            converted.tintingSides[sideKey] = { ...converted[sideKey] };
                        }
                    });

                    // Keep flat keys available for legacy UI/appointment consumers.
                    Object.keys(converted.tintingSides).forEach(sideKey => {
                        if (!converted[sideKey] || typeof converted[sideKey] !== 'object') {
                            converted[sideKey] = { ...converted.tintingSides[sideKey] };
                        }
                    });

                    return converted;
                }) || [{ heatRejection: '', darknessLevels: [], tintingSides: {} }];

                setData(convertedData.sort((a, b) => Number(a.heatRejection.replace("%", '')) > Number(b.heatRejection.replace("%", '')) ? 1 : -1))
            }
        } else {
            // Reset to initial data when adding new variation
            if (isPPFService) {
                setData([{ warranties: {}, tintingSides: {}, finish: null, modelSeries: { name: '', name_ar: '' } }]);
            } else {
                setData([{ heatRejection: '', darknessLevels: [], tintingSides: {} }]);
            }
        }
        setVariation(selectedVariation)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVariation, editVariation, isPPFService])



    const handleDateChange = (e, itemIndex, type) => {
        let newVariation = JSON.parse(JSON.stringify(variation))
        let newData = JSON.parse(JSON.stringify(data))

        // Check if 'type' exists in newData[itemIndex], if not, initialize it
        if (!newData[itemIndex][type]) {
            newData[itemIndex][type] = {};
        }

        newData[itemIndex][type][carKey] = e.target.value;

        // Keep new tintingSides schema in sync with legacy flat fields for window tint.
        if (!isPPFService) {
            if (!newData[itemIndex].tintingSides) {
                newData[itemIndex].tintingSides = {};
            }
            if (!newData[itemIndex].tintingSides[type]) {
                newData[itemIndex].tintingSides[type] = {};
            }
            newData[itemIndex].tintingSides[type][carKey] = e.target.value;
        }

        setData(newData);
        newVariation.data = newData;
        setVariation(newVariation);
    };

    const handleWarrantyPriceChange = (e, itemIndex, warrantyValue) => {
        let newVariation = JSON.parse(JSON.stringify(variation))
        let newData = JSON.parse(JSON.stringify(data))

        // Initialize warranties object if it doesn't exist
        if (!newData[itemIndex].warranties) {
            newData[itemIndex].warranties = {};
        }

        // Initialize warranty entry if it doesn't exist
        if (!newData[itemIndex].warranties[warrantyValue]) {
            newData[itemIndex].warranties[warrantyValue] = {};
        }

        newData[itemIndex].warranties[warrantyValue][carKey] = e.target.value;

        setData(newData);
        newVariation.data = newData;
        setVariation(newVariation);
    };

    const handleAddWarranty = (itemIndex, warrantyValue) => {
        let newVariation = JSON.parse(JSON.stringify(variation))
        let newData = JSON.parse(JSON.stringify(data))

        // Initialize warranties object if it doesn't exist
        if (!newData[itemIndex].warranties) {
            newData[itemIndex].warranties = {};
        }

        // If warranty doesn't exist, create it
        if (!newData[itemIndex].warranties[warrantyValue]) {
            newData[itemIndex].warranties[warrantyValue] = {};
        }

        // Add or update price for current carKey only (preserve other car types if they exist)
        // Always set it to empty string if it doesn't exist or is undefined/null
        if (newData[itemIndex].warranties[warrantyValue][carKey] === undefined || newData[itemIndex].warranties[warrantyValue][carKey] === null) {
            newData[itemIndex].warranties[warrantyValue][carKey] = '';
        }

        setData(newData);
        newVariation.data = newData;
        setVariation(newVariation);
    };

    const handleRemoveWarranty = (itemIndex, warrantyValue) => {
        let newVariation = JSON.parse(JSON.stringify(variation))
        let newData = JSON.parse(JSON.stringify(data))

        if (newData[itemIndex].warranties && newData[itemIndex].warranties[warrantyValue]) {
            // Remove only the price for current carKey
            if (newData[itemIndex].warranties[warrantyValue][carKey] !== undefined && newData[itemIndex].warranties[warrantyValue][carKey] !== null) {
                delete newData[itemIndex].warranties[warrantyValue][carKey];
            }

            // If no other car types have prices, remove the entire warranty entry
            const remainingCarKeys = Object.keys(newData[itemIndex].warranties[warrantyValue] || {});
            if (remainingCarKeys.length === 0) {
                delete newData[itemIndex].warranties[warrantyValue];
            }
        }

        setData(newData);
        newVariation.data = newData;
        setVariation(newVariation);
    };

    const handleTintingSidePriceChange = (e, itemIndex, sideKey) => {
        let newVariation = JSON.parse(JSON.stringify(variation))
        let newData = JSON.parse(JSON.stringify(data))

        // Initialize tintingSides object if it doesn't exist
        if (!newData[itemIndex].tintingSides) {
            newData[itemIndex].tintingSides = {};
        }

        // Initialize side entry if it doesn't exist
        if (!newData[itemIndex].tintingSides[sideKey]) {
            newData[itemIndex].tintingSides[sideKey] = {};
        }

        newData[itemIndex].tintingSides[sideKey][carKey] = e.target.value;

        setData(newData);
        newVariation.data = newData;
        setVariation(newVariation);
    };

    const handleAddTintingSide = (itemIndex, sideKey) => {
        let newVariation = JSON.parse(JSON.stringify(variation))
        let newData = JSON.parse(JSON.stringify(data))

        // Initialize tintingSides object if it doesn't exist
        if (!newData[itemIndex].tintingSides) {
            newData[itemIndex].tintingSides = {};
        }

        // If side doesn't exist, create it with empty price for current car type
        if (!newData[itemIndex].tintingSides[sideKey]) {
            newData[itemIndex].tintingSides[sideKey] = {};
        }

        // Add or update price for current carKey only (preserve other car types if they exist)
        // Always set it to empty string if it doesn't exist or is undefined/null
        if (newData[itemIndex].tintingSides[sideKey][carKey] === undefined || newData[itemIndex].tintingSides[sideKey][carKey] === null) {
            newData[itemIndex].tintingSides[sideKey][carKey] = '';
        }

        setData(newData);
        newVariation.data = newData;
        setVariation(newVariation);
    };

    const handleRemoveTintingSide = (itemIndex, sideKey) => {
        let newVariation = JSON.parse(JSON.stringify(variation))
        let newData = JSON.parse(JSON.stringify(data))

        if (newData[itemIndex].tintingSides && newData[itemIndex].tintingSides[sideKey]) {
            // Remove only the price for current carKey
            if (newData[itemIndex].tintingSides[sideKey][carKey] !== undefined && newData[itemIndex].tintingSides[sideKey][carKey] !== null) {
                delete newData[itemIndex].tintingSides[sideKey][carKey];
            }

            // If no other car types have prices, remove the entire side entry
            const remainingCarKeys = Object.keys(newData[itemIndex].tintingSides[sideKey] || {});
            if (remainingCarKeys.length === 0) {
                delete newData[itemIndex].tintingSides[sideKey];
            }
        }

        setData(newData);
        newVariation.data = newData;
        setVariation(newVariation);
    };

    let UpdateVariation = () => {
        // Add variationType based on service type
        const variationToSave = { ...variation };
        variationToSave.variationType = isPPFService ? 'ppf' : 'windowTint';

        // Flatten tintingSides to old format before saving (don't save tintingSides object)
        if (variationToSave.data && Array.isArray(variationToSave.data)) {
            variationToSave.data = variationToSave.data.map(item => {
                const flattenedItem = { ...item };

                // If tintingSides exists, flatten it to the old format
                if (flattenedItem.tintingSides && typeof flattenedItem.tintingSides === 'object') {
                    Object.keys(flattenedItem.tintingSides).forEach(sideKey => {
                        // Convert tintingSides[sideKey] to item[sideKey] format
                        if (flattenedItem.tintingSides[sideKey] && typeof flattenedItem.tintingSides[sideKey] === 'object') {
                            flattenedItem[sideKey] = flattenedItem.tintingSides[sideKey];
                        }
                    });
                    // Remove tintingSides object as we're using flattened format
                    delete flattenedItem.tintingSides;
                }

                return flattenedItem;
            });
        }

        if (addVariation) {
            let key = firestore().collection('tintingVariations').doc().id
            delete variationToSave.label
            delete variationToSave.value

            variationToSave.key = key
            variationToSave.sp_id = selectedWasher.sp_id
            variationToSave.worker_id = selectedWasher.key
            firestore().collection('tintingVariations').doc(key).set(variationToSave).then(() => {
                dismiss(key)
            })
        } else {
            firestore().collection('tintingVariations').doc(variationToSave?.key).update(variationToSave).then(() => {
                if (availableVariation) {
                    addAvailableVariation()
                } else {
                    dismiss()
                }

            })
        }

    }
    let active = data?.reduce((value, currentValue) => {
        if (!value) return false; // Early exit if already invalid

        if (isPPFService) {
            // PPF validation: warranties (optional, but if added, ALL must have prices), finish, modelSeries required; side prices optional

            // Check warranties for current carKey - optional, but if any are added for this carKey, ALL must have prices
            if (currentValue.warranties && typeof currentValue.warranties === 'object') {
                // Filter to only warranties that have the current carKey property set
                const warrantiesWithCarKey = Object.keys(currentValue.warranties).filter(warrantyKey =>
                    currentValue.warranties[warrantyKey] &&
                    typeof currentValue.warranties[warrantyKey] === 'object' &&
                    currentValue.warranties[warrantyKey][carKey] !== undefined &&
                    currentValue.warranties[warrantyKey][carKey] !== null
                );

                // If warranties are added for this carKey, validate that ALL have prices
                if (warrantiesWithCarKey.length > 0) {
                    for (const warrantyKey of warrantiesWithCarKey) {
                        if (currentValue.warranties[warrantyKey][carKey] === '' ||
                            currentValue.warranties[warrantyKey][carKey] === null ||
                            currentValue.warranties[warrantyKey][carKey] === undefined) {
                            return false; // Every added warranty for this carKey must have a price
                        }
                    }
                }
            }

            // Check finish
            if (!currentValue.finish || (typeof currentValue.finish === 'object' && !currentValue.finish.key) || (typeof currentValue.finish === 'string' && currentValue.finish === '')) {
                return false;
            }

            // Check modelSeries
            if (!currentValue.modelSeries || typeof currentValue.modelSeries !== 'object' || !currentValue.modelSeries.name || currentValue.modelSeries.name === '' || !currentValue.modelSeries.name_ar || currentValue.modelSeries.name_ar === '') {
                return false;
            }

            // Tinting side prices optional — providers may only support specific sides.
        } else {
            // Window tint validation: heat rejection + tint levels required; side prices optional per provider.

            // Check heatRejection
            if (!currentValue.heatRejection || currentValue.heatRejection === '') {
                return false;
            }

            // Check darknessLevels
            if (!currentValue.darknessLevels || !Array.isArray(currentValue.darknessLevels) || currentValue.darknessLevels.length === 0) {
                return false;
            }

            // Side prices optional — same as PPF: not every side on the service must be priced.
        }

        return value;

    }, true)

    return (
        <>
            <style>
                {`
                    .modal-shadow .modal-dialog {
                        box-shadow: 0 10px 40px rgba(0,0,0,0.2) !important;
                    }
                `}
            </style>
            <Modal
                size='lg'
                isOpen={addVariation || editVariation}
                style={{ maxWidth: '1200px' }}
                backdrop="static"
                className="modal-shadow">
                <ModalHeader style={{
                    padding: '20px 25px',
                    borderBottom: '1px solid #e9ecef',
                    background: '#f8f9fa'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <i className="simple-icon-settings" style={{
                            color: '#3498db',
                            fontSize: '20px'
                        }}></i>
                        <h3 style={{
                            margin: 0,
                            fontWeight: '600',
                            color: '#2c3e50',
                            fontSize: '18px'
                        }}>
                            {editVariation ? 'Edit Variation' : 'Add Variation'}
                        </h3>
                    </div>
                    <p style={{
                        margin: '5px 0 0 0',
                        fontSize: '13px',
                        color: '#6c757d',
                        fontWeight: '400'
                    }}>
                        Configure vehicle type price, duration and variations
                    </p>
                </ModalHeader>
                <ModalBody style={{ padding: '25px' }}>
                    <div style={{
                        marginBottom: '20px',
                        paddingBottom: '15px',
                        borderBottom: '1px solid #e9ecef'
                    }}>
                        <h4 style={{
                            fontWeight: '600',
                            color: '#2c3e50',
                            fontSize: '16px',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <i className="simple-icon-info" style={{ color: '#3498db', fontSize: '16px' }}></i>
                            Basic Information
                        </h4>
                        <Row>
                            <Colxx xxs="6" className="mt-2" >
                                <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                    <i className="simple-icon-tag" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                    * Name
                                </Label>
                                <Input className="customInput"
                                    value={variation?.name}
                                    placeholder="e.g. China" type="text" onChange={(e) => {
                                        let newVariation = JSON.parse(JSON.stringify(variation))
                                        newVariation.name = e.target.value
                                        setVariation(newVariation)
                                    }} />
                            </Colxx>
                            <Colxx xxs="6" className="mt-2" >
                                <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                    <i className="simple-icon-tag" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                    * Arabic Name
                                </Label>
                                <Input className="customInput"
                                    value={variation?.name_ar}
                                    placeholder="e.g. China" type="text" onChange={(e) => {
                                        let newVariation = JSON.parse(JSON.stringify(variation))
                                        newVariation.name_ar = e.target.value
                                        setVariation(newVariation)
                                    }} />
                            </Colxx>
                        </Row>
                        <Row>
                            <Colxx xxs="6" className="mt-3" >
                                <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                    <i className="simple-icon-globe" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                    Made in
                                </Label>
                                <Select
                                    components={{ Input: CustomSelectInput }}
                                    className="react-select"
                                    name="form-field-name"
                                    value={variation?.made_in ? { value: variation?.made_in, label: variation?.made_in, key: variation?.made_in } : ''}
                                    placeholder="e.g. China"
                                    onChange={(e) => {
                                        let newVariation = JSON.parse(JSON.stringify(variation))
                                        newVariation.made_in = e.value
                                        setVariation(newVariation)

                                    }}
                                    options={service.madeIn.map(option => { return option !== variation?.made_in && { value: option, label: option, key: option } }).filter(rec => rec)}
                                />

                            </Colxx>
                            <Colxx xxs="6" className="mt-3">
                                <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                    <i className="simple-icon-badge" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                    Brand
                                </Label>
                                <Select
                                    components={{ Input: CustomSelectInput }}
                                    className="react-select"
                                    name="form-field-name"
                                    value={variation?.brand ? { value: variation?.brand, label: variation?.brand, key: variation?.brand } : ''}
                                    placeholder="e.g. V-Kool"
                                    onChange={(e) => {
                                        let newVariation = JSON.parse(JSON.stringify(variation))
                                        newVariation.brand = e.value
                                        setVariation(newVariation)

                                    }}
                                    options={service.brands.map(option => { return option !== variation?.brand && { value: option, label: option, key: option } }).filter(rec => rec)}
                                />
                            </Colxx>
                        </Row>
                        {isPPFService && (
                            <Row>
                                <Colxx xxs="6" className="mt-3">
                                    <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                        <i className="simple-icon-shield" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                        Default Warranty
                                    </Label>
                                    <Select
                                        components={{ Input: CustomSelectInput }}
                                        className="react-select"
                                        name="form-field-name"
                                        value={variation?.defaultWarranty ? { value: variation?.defaultWarranty, label: variation?.defaultWarranty, key: variation?.defaultWarranty } : ''}
                                        placeholder="Select Default Warranty"
                                        onChange={(e) => {
                                            let newVariation = JSON.parse(JSON.stringify(variation))
                                            newVariation.defaultWarranty = e.value
                                            setVariation(newVariation)
                                        }}
                                        options={service?.warranty?.map(option => { return option !== variation?.defaultWarranty && { value: option, label: option, key: option } }).filter(rec => rec)}
                                    />
                                </Colxx>
                            </Row>
                        )}
                    </div>
                    <div style={{
                        marginTop: '25px',
                        marginBottom: '20px',
                        paddingBottom: '15px',
                        borderBottom: '1px solid #e9ecef'
                    }}>
                        <h4 style={{
                            fontWeight: '600',
                            color: '#2c3e50',
                            fontSize: '16px',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <i className="simple-icon-doc" style={{ color: '#3498db', fontSize: '16px' }}></i>
                            Descriptions
                        </h4>
                        <Row>
                            <Colxx xxs="6" className="mt-2">
                                <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                    <i className="simple-icon-note" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                    Description
                                </Label>
                                <Creatable
                                    components={{ Input: CustomSelectInput }}
                                    className="react-select"
                                    name="form-field-name"
                                    value={''}
                                    options={(service?.description || [])?.map(item => ({ label: item, value: item, key: item }))?.filter(item => {
                                        const isOptionUnavailable = variation?.description?.includes(item.key);
                                        return !isOptionUnavailable;
                                    })}
                                    onChange={(e) => {
                                        if (e && e.value) {
                                            let newVariation = JSON.parse(JSON.stringify(variation))
                                            let newDescriptions = newVariation?.description || []
                                            // Check if description already exists to avoid duplicates
                                            if (!newDescriptions.includes(e.value)) {
                                                newDescriptions.push(e.value)
                                                newVariation.description = newDescriptions
                                                setVariation(newVariation)
                                            }
                                        }
                                    }}
                                    onCreateOption={(inputValue) => {
                                        // Add the new description immediately when created
                                        const trimmedValue = inputValue.trim();
                                        if (trimmedValue && isAdmin) {
                                            let newVariation = JSON.parse(JSON.stringify(variation))
                                            let newDescriptions = newVariation?.description || []
                                            // Check if description already exists to avoid duplicates
                                            if (!newDescriptions.includes(trimmedValue)) {
                                                newDescriptions.push(trimmedValue)
                                                newVariation.description = newDescriptions
                                                setVariation(newVariation)
                                            }
                                        }
                                        // Return the new option object for react-select
                                        return { label: trimmedValue, value: trimmedValue, key: trimmedValue };
                                    }}
                                    isValidNewOption={(inputValue) => {
                                        // Only allow creating new options if admin and input is not empty
                                        // Also check if it doesn't already exist in selected descriptions
                                        const trimmedValue = inputValue.trim();
                                        const alreadyExists = variation?.description?.includes(trimmedValue);
                                        return isAdmin && trimmedValue.length > 0 && !alreadyExists;
                                    }}
                                    placeholder="Add Description"
                                />
                                {(variation?.description)?.length ? <div className="mt-2"
                                    style={{
                                        minHeight: 'auto',
                                        borderRadius: '3px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        flexWrap: 'wrap',
                                    }}>
                                    {(variation?.description)?.map((item, index) => {
                                        return (
                                            <div key={index} style={{
                                                margin: '4px 4px 4px 0',
                                                padding: '6px 12px',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: '#f8f9fa',
                                                transition: 'all 0.15s ease'
                                            }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.borderColor = '#3498db';
                                                    e.currentTarget.style.background = '#e3f2fd';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                                    e.currentTarget.style.background = '#f8f9fa';
                                                }}
                                            >
                                                <div style={{ marginRight: '8px', fontSize: '13px', color: '#2c3e50' }}>{item}</div>
                                                <i style={{
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    color: '#dc3545',
                                                    transition: 'all 0.2s'
                                                }}
                                                    className="simple-icon-close"
                                                    onClick={() => {
                                                        let newVariation = JSON.parse(JSON.stringify(variation))

                                                        let newDescriptions = newVariation?.description.filter((deletedItem) => {
                                                            return (deletedItem !== item)
                                                        })
                                                        newVariation.description = newDescriptions
                                                        setVariation(newVariation)
                                                    }} />
                                            </div>
                                        )
                                    })}
                                </div> : ""
                                }
                            </Colxx>
                            <Colxx xxs="6" className="mt-2" >
                                <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                    <i className="simple-icon-note" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                                    Arabic Description
                                </Label>
                                <Creatable
                                    components={{ Input: CustomSelectInput }}
                                    className="react-select"
                                    placeholder="Add Arabic Description"
                                    value={''}
                                    options={(service?.description_ar || [])?.map(item => ({ label: item, value: item, key: item }))?.filter(item => {
                                        const isOptionUnavailable = variation?.description_ar?.includes(item.key);
                                        return !isOptionUnavailable;
                                    })}
                                    name="form-field-name"
                                    onChange={(e) => {
                                        if (e && e.value) {
                                            let newVariation = JSON.parse(JSON.stringify(variation))
                                            let newDescriptions = newVariation?.description_ar || []
                                            // Check if description already exists to avoid duplicates
                                            if (!newDescriptions.includes(e.value)) {
                                                newDescriptions.push(e.value)
                                                newVariation.description_ar = newDescriptions
                                                setVariation(newVariation)
                                            }
                                        }
                                    }}
                                    onCreateOption={(inputValue) => {
                                        // Add the new description immediately when created
                                        const trimmedValue = inputValue.trim();
                                        if (trimmedValue && isAdmin) {
                                            let newVariation = JSON.parse(JSON.stringify(variation))
                                            let newDescriptions = newVariation?.description_ar || []
                                            // Check if description already exists to avoid duplicates
                                            if (!newDescriptions.includes(trimmedValue)) {
                                                newDescriptions.push(trimmedValue)
                                                newVariation.description_ar = newDescriptions
                                                setVariation(newVariation)
                                            }
                                        }
                                        // Return the new option object for react-select
                                        return { label: trimmedValue, value: trimmedValue, key: trimmedValue };
                                    }}
                                    isValidNewOption={(inputValue) => {
                                        // Only allow creating new options if admin and input is not empty
                                        // Also check if it doesn't already exist in selected descriptions
                                        const trimmedValue = inputValue.trim();
                                        const alreadyExists = variation?.description_ar?.includes(trimmedValue);
                                        return isAdmin && trimmedValue.length > 0 && !alreadyExists;
                                    }}
                                />
                                {(variation?.description_ar)?.length ? <div className="mt-2"
                                    style={{
                                        minHeight: 'auto',
                                        borderRadius: '3px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        flexWrap: 'wrap',
                                    }}>
                                    {variation?.description_ar?.map((item, index) => {
                                        return (
                                            <div key={index} style={{
                                                margin: '4px 4px 4px 0',
                                                padding: '6px 12px',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: '#f8f9fa',
                                                transition: 'all 0.15s ease'
                                            }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.borderColor = '#3498db';
                                                    e.currentTarget.style.background = '#e3f2fd';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                                    e.currentTarget.style.background = '#f8f9fa';
                                                }}
                                            >
                                                <div style={{ marginRight: '8px', fontSize: '13px', color: '#2c3e50' }}>{item}</div>
                                                <i style={{
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    color: '#dc3545',
                                                    transition: 'all 0.2s'
                                                }}
                                                    className="simple-icon-close"
                                                    onClick={() => {
                                                        let newVariation = JSON.parse(JSON.stringify(variation))

                                                        let newDescriptions = newVariation?.description_ar.filter((deletedItem) => {
                                                            return (deletedItem !== item)
                                                        })
                                                        newVariation.description_ar = newDescriptions
                                                        setVariation(newVariation)

                                                    }} />
                                            </div>
                                        )
                                    })}
                                </div> : ""
                                }
                            </Colxx>
                        </Row>
                    </div>

                    <div style={{
                        marginTop: '25px',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '20px'
                        }}>
                            <h4 style={{
                                fontWeight: '600',
                                color: '#2c3e50',
                                fontSize: '16px',
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <i className="simple-icon-list" style={{ color: '#3498db', fontSize: '16px' }}></i>
                                Options
                            </h4>
                            <div style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#3498db',
                                cursor: 'pointer',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                border: '1px solid #3498db',
                                background: '#e3f2fd',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.15s ease'
                            }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#3498db';
                                    e.currentTarget.style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#e3f2fd';
                                    e.currentTarget.style.color = '#3498db';
                                }}
                                onClick={() => {
                                    let newData = JSON.parse(JSON.stringify(data))
                                    if (isPPFService) {
                                        // For PPF, add warranties (with pricing), tintingSides (with pricing), finish, and modelSeries fields
                                        // Finish is stored as object with key, name, and name_ar (similar to tintingSides)
                                        // Warranties is an object where keys are warranty values and values contain pricing for each car type
                                        // TintingSides is an object where keys are side keys and values contain pricing for each car type
                                        // ModelSeries is stored as object with name and name_ar
                                        newData.push({ warranties: {}, tintingSides: {}, finish: null, modelSeries: { name: '', name_ar: '' } })
                                    } else {
                                        newData.push({ heatRejection: '', darknessLevels: [], tintingSides: {} })
                                    }
                                    setData(newData)

                                    let newVariation = JSON.parse(JSON.stringify(variation))
                                    newVariation.data = newData
                                    setVariation(newVariation)
                                }}>
                                <i className="simple-icon-plus" style={{ fontSize: '12px' }} />
                                Add variation
                            </div>
                        </div>
                        <Row>
                            <Colxx xxs="12" className="mt-2">
                                {data && data?.length ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        {data?.map((item, itemIndex) => (
                                            <Card key={itemIndex} style={{
                                                borderRadius: '8px',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                border: '1px solid #e9ecef'
                                            }}>
                                                <CardBody style={{ padding: '20px' }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        marginBottom: '20px',
                                                        paddingBottom: '15px',
                                                        borderBottom: '1px solid #e9ecef'
                                                    }}>
                                                        <h5 style={{
                                                            margin: 0,
                                                            fontWeight: '600',
                                                            color: '#2c3e50',
                                                            fontSize: '16px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px'
                                                        }}>
                                                            <i className="simple-icon-list" style={{ color: '#3498db', fontSize: '16px' }}></i>
                                                            Option {itemIndex + 1}
                                                        </h5>
                                                        {data.length > 1 && (
                                                            <Button
                                                                color="danger"
                                                                outline
                                                                size="sm"
                                                                onClick={() => {
                                                                    let newData = JSON.parse(JSON.stringify(data)).filter((rec, idx) => idx !== itemIndex)
                                                                    if (!newData?.length) {
                                                                        // If no variations left, create a new one
                                                                        if (isPPFService) {
                                                                            newData = [{ warranties: {}, tintingSides: {}, finish: null, modelSeries: { name: '', name_ar: '' } }];
                                                                        } else {
                                                                            newData = [{ heatRejection: '', darknessLevels: [], tintingSides: {} }];
                                                                        }
                                                                    }
                                                                    setData(newData)
                                                                    let newVariation = JSON.parse(JSON.stringify(variation))
                                                                    newVariation.data = newData
                                                                    setVariation(newVariation)
                                                                }}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px'
                                                                }}
                                                            >
                                                                <i className="simple-icon-close" style={{ fontSize: '12px' }}></i>
                                                                Remove
                                                            </Button>
                                                        )}
                                                    </div>
                                                    <Row>
                                                        {isPPFService ? (
                                                            <>

                                                                <Colxx xxs="12" md="4" className="mb-3">
                                                                    <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                                                        <i className="simple-icon-layers" style={{ marginRight: '6px', fontSize: '12px', color: '#3498db' }}></i>
                                                                        * Model/Series (English)
                                                                    </Label>
                                                                    <Input
                                                                        className="customInput"
                                                                        value={typeof item.modelSeries === 'object' ? (item.modelSeries?.name || '') : ''}
                                                                        placeholder="e.g. Model X"
                                                                        type="text"
                                                                        onChange={(e) => {
                                                                            let newData = JSON.parse(JSON.stringify(data))
                                                                            if (typeof newData[itemIndex].modelSeries !== 'object' || !newData[itemIndex].modelSeries) {
                                                                                newData[itemIndex].modelSeries = { name: '', name_ar: '' }
                                                                            }
                                                                            newData[itemIndex].modelSeries.name = e.target.value
                                                                            setData(newData)
                                                                            let newVariation = JSON.parse(JSON.stringify(variation))
                                                                            newVariation.data = newData
                                                                            setVariation(newVariation)
                                                                        }}
                                                                    />
                                                                </Colxx>
                                                                <Colxx xxs="12" md="4" className="mb-3">
                                                                    <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                                                        <i className="simple-icon-layers" style={{ marginRight: '6px', fontSize: '12px', color: '#3498db' }}></i>
                                                                        * Model/Series (Arabic)
                                                                    </Label>
                                                                    <Input
                                                                        className="customInput"
                                                                        value={typeof item.modelSeries === 'object' ? (item.modelSeries?.name_ar || '') : ''}
                                                                        placeholder="e.g. موديل X"
                                                                        type="text"
                                                                        onChange={(e) => {
                                                                            let newData = JSON.parse(JSON.stringify(data))
                                                                            if (typeof newData[itemIndex].modelSeries !== 'object' || !newData[itemIndex].modelSeries) {
                                                                                newData[itemIndex].modelSeries = { name: '', name_ar: '' }
                                                                            }
                                                                            newData[itemIndex].modelSeries.name_ar = e.target.value
                                                                            setData(newData)
                                                                            let newVariation = JSON.parse(JSON.stringify(variation))
                                                                            newVariation.data = newData
                                                                            setVariation(newVariation)
                                                                        }}
                                                                    />
                                                                </Colxx>
                                                                <Colxx xxs="12" md="4" className="mb-3">
                                                                    <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                                                        <i className="simple-icon-diamond" style={{ marginRight: '6px', fontSize: '12px', color: '#9b59b6' }}></i>
                                                                        * Finish
                                                                    </Label>
                                                                    <Select
                                                                        components={{ Input: CustomSelectInput }}
                                                                        className="react-select"
                                                                        name="form-field-name"
                                                                        value={item.finish ? (typeof item.finish === 'object' && item.finish.key ? { value: item.finish.key, label: item.finish.name, key: item.finish.key } : { value: item.finish, label: item.finish, key: item.finish }) : ''}
                                                                        placeholder="Select Finish"
                                                                        onChange={(e) => {
                                                                            let newData = JSON.parse(JSON.stringify(data))
                                                                            const selectedFinish = service?.finish?.find(f => f.key === e.key) || { key: e.key, name: e.label, name_ar: '' }
                                                                            newData[itemIndex].finish = selectedFinish
                                                                            setData(newData)
                                                                            let newVariation = JSON.parse(JSON.stringify(variation))
                                                                            newVariation.data = newData
                                                                            setVariation(newVariation)
                                                                        }}
                                                                        options={service?.finish?.map(option => ({ value: option.key, label: option.name, key: option.key }))}
                                                                    />
                                                                </Colxx>
                                                                <Colxx xxs="12" className="mb-3">
                                                                    <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '12px', display: 'block' }}>
                                                                        <i className="simple-icon-shield" style={{ marginRight: '6px', fontSize: '12px', color: '#27ae60' }}></i>
                                                                        Warranties ({carType})
                                                                        <span style={{ color: '#6c757d', fontWeight: '400', fontSize: '12px', marginLeft: '6px' }}>(Optional)</span>
                                                                    </Label>
                                                                    <div style={{ marginBottom: '12px' }}>
                                                                        <Select
                                                                            components={{ Input: CustomSelectInput }}
                                                                            className="react-select"
                                                                            name="form-field-name"
                                                                            value=""
                                                                            placeholder="Add Warranty"
                                                                            onChange={(e) => {
                                                                                handleAddWarranty(itemIndex, e.value);
                                                                            }}
                                                                            options={service?.warranty?.filter(w => !item.warranties || !item.warranties[w] || item.warranties[w][carKey] === undefined || item.warranties[w][carKey] === null).map(option => ({ value: option, label: option, key: option }))}
                                                                        />
                                                                    </div>
                                                                    {item.warranties && Object.keys(item.warranties).filter(w => item.warranties[w] && item.warranties[w][carKey] !== undefined && item.warranties[w][carKey] !== null).length > 0 ? (
                                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                                            {Object.keys(item.warranties).filter(w => item.warranties[w] && item.warranties[w][carKey] !== undefined && item.warranties[w][carKey] !== null).map(warrantyValue => (
                                                                                <div key={warrantyValue} style={{
                                                                                    padding: '12px',
                                                                                    border: '1px solid #e0e0e0',
                                                                                    borderRadius: '8px',
                                                                                    background: '#f8f9fa',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: '12px',
                                                                                    transition: 'all 0.15s ease',
                                                                                    flex: '1 1 auto',
                                                                                    minWidth: '250px'
                                                                                }}
                                                                                    onMouseEnter={(e) => {
                                                                                        e.currentTarget.style.borderColor = '#3498db';
                                                                                        e.currentTarget.style.background = '#e3f2fd';
                                                                                    }}
                                                                                    onMouseLeave={(e) => {
                                                                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                                                                        e.currentTarget.style.background = '#f8f9fa';
                                                                                    }}
                                                                                >
                                                                                    <div style={{
                                                                                        flex: '1 1 auto',
                                                                                        fontSize: '14px',
                                                                                        fontWeight: '500',
                                                                                        color: '#2c3e50',
                                                                                        minWidth: '100px'
                                                                                    }}>{warrantyValue}</div>
                                                                                    <Input
                                                                                        className="customInput"
                                                                                        type="number"
                                                                                        value={item.warranties[warrantyValue]?.[carKey] || ''}
                                                                                        placeholder="e.g. 100 AED"
                                                                                        style={{ width: '150px', flexShrink: 0 }}
                                                                                        onChange={(e) => handleWarrantyPriceChange(e, itemIndex, warrantyValue)}
                                                                                    />
                                                                                    <Button
                                                                                        color="danger"
                                                                                        outline
                                                                                        size="sm"
                                                                                        onClick={() => handleRemoveWarranty(itemIndex, warrantyValue)}
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            justifyContent: 'center',
                                                                                            minWidth: '32px',
                                                                                            height: '32px',
                                                                                            padding: 0,
                                                                                            flexShrink: 0
                                                                                        }}
                                                                                    >
                                                                                        <i className="simple-icon-close" style={{ fontSize: '12px' }}></i>
                                                                                    </Button>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <div style={{
                                                                            padding: '20px',
                                                                            textAlign: 'center',
                                                                            color: '#6c757d',
                                                                            fontSize: '14px',
                                                                            border: '1px dashed #e0e0e0',
                                                                            borderRadius: '8px',
                                                                            background: '#f8f9fa'
                                                                        }}>
                                                                            No warranties added. Select a warranty from the dropdown above.
                                                                        </div>
                                                                    )}
                                                                </Colxx>

                                                                <Colxx xxs="12" className="mb-3">
                                                                    <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '12px', display: 'block' }}>
                                                                        <i className="simple-icon-wallet" style={{ marginRight: '6px', fontSize: '12px', color: '#27ae60' }}></i>
                                                                        * Side Prices ({carType})
                                                                    </Label>
                                                                    <div style={{ marginBottom: '12px' }}>
                                                                        <Select
                                                                            components={{ Input: CustomSelectInput }}
                                                                            className="react-select"
                                                                            name="form-field-name"
                                                                            value=""
                                                                            placeholder="Add Tinting Side"
                                                                            onChange={(e) => {
                                                                                handleAddTintingSide(itemIndex, e.key);
                                                                            }}
                                                                            options={service?.tintingSides?.filter(side => !item.tintingSides || !item.tintingSides[side.key] || item.tintingSides[side.key][carKey] === undefined || item.tintingSides[side.key][carKey] === null).map(option => ({ value: option.key, label: option.name, key: option.key }))}
                                                                        />
                                                                    </div>
                                                                    {item.tintingSides && Object.keys(item.tintingSides).filter(s => item.tintingSides[s] && item.tintingSides[s][carKey] !== undefined && item.tintingSides[s][carKey] !== null).length > 0 ? (
                                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                                            {Object.keys(item.tintingSides).filter(s => item.tintingSides[s] && item.tintingSides[s][carKey] !== undefined && item.tintingSides[s][carKey] !== null).map(sideKey => {
                                                                                const side = service?.tintingSides?.find(s => s.key === sideKey);
                                                                                return (
                                                                                    <div key={sideKey} style={{
                                                                                        padding: '12px',
                                                                                        border: '1px solid #e0e0e0',
                                                                                        borderRadius: '8px',
                                                                                        background: '#f8f9fa',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: '12px',
                                                                                        transition: 'all 0.15s ease',
                                                                                        flex: '1 1 auto',
                                                                                        minWidth: '250px'
                                                                                    }}
                                                                                        onMouseEnter={(e) => {
                                                                                            e.currentTarget.style.borderColor = '#3498db';
                                                                                            e.currentTarget.style.background = '#e3f2fd';
                                                                                        }}
                                                                                        onMouseLeave={(e) => {
                                                                                            e.currentTarget.style.borderColor = '#e0e0e0';
                                                                                            e.currentTarget.style.background = '#f8f9fa';
                                                                                        }}
                                                                                    >
                                                                                        <div style={{
                                                                                            flex: '1 1 auto',
                                                                                            fontSize: '14px',
                                                                                            fontWeight: '500',
                                                                                            color: '#2c3e50',
                                                                                            minWidth: '100px'
                                                                                        }}>{side?.name || sideKey}</div>
                                                                                        <Input
                                                                                            className="customInput"
                                                                                            type="number"
                                                                                            value={item.tintingSides[sideKey]?.[carKey] || ''}
                                                                                            placeholder="e.g. 10 AED"
                                                                                            style={{ width: '150px', flexShrink: 0 }}
                                                                                            onChange={(e) => handleTintingSidePriceChange(e, itemIndex, sideKey)}
                                                                                        />
                                                                                        <Button
                                                                                            color="danger"
                                                                                            outline
                                                                                            size="sm"
                                                                                            onClick={() => handleRemoveTintingSide(itemIndex, sideKey)}
                                                                                            style={{
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                justifyContent: 'center',
                                                                                                minWidth: '32px',
                                                                                                height: '32px',
                                                                                                padding: 0,
                                                                                                flexShrink: 0
                                                                                            }}
                                                                                        >
                                                                                            <i className="simple-icon-close" style={{ fontSize: '12px' }}></i>
                                                                                        </Button>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    ) : (
                                                                        <div style={{
                                                                            padding: '20px',
                                                                            textAlign: 'center',
                                                                            color: '#6c757d',
                                                                            fontSize: '14px',
                                                                            border: '1px dashed #e0e0e0',
                                                                            borderRadius: '8px',
                                                                            background: '#f8f9fa'
                                                                        }}>
                                                                            No tinting sides added. Select a side from the dropdown above.
                                                                        </div>
                                                                    )}
                                                                </Colxx>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Colxx xxs="12" md="4" className="mb-3">
                                                                    <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                                                        <i className="simple-icon-fire" style={{ marginRight: '6px', fontSize: '12px', color: '#e74c3c' }}></i>
                                                                        * Heat Rejection
                                                                    </Label>
                                                                    <Select
                                                                        components={{ Input: CustomSelectInput }}
                                                                        className="react-select"
                                                                        name="form-field-name"
                                                                        value={item.heatRejection ? { value: item.heatRejection, label: item.heatRejection, key: item.heatRejection } : ''}
                                                                        placeholder="e.g. 10%"
                                                                        onChange={(e) => {
                                                                            let newData = JSON.parse(JSON.stringify(data))
                                                                            newData[itemIndex].heatRejection = e.key
                                                                            setData(newData)
                                                                            let newVariation = JSON.parse(JSON.stringify(variation))
                                                                            newVariation.data = newData
                                                                            setVariation(newVariation)
                                                                        }}
                                                                        options={service?.heatLevels?.filter(rec => !variation?.data?.map(heat => heat?.heatRejection)?.includes(rec))?.sort((a, b) => Number(a.replace("%", '')) > Number(b.replace("%", '')) ? 1 : -1)?.map(option => { return data.heatRejection !== option && { value: option, label: option, key: option } }).filter(rec => rec)}
                                                                    />
                                                                </Colxx>
                                                                <Colxx xxs="12" md="4" className="mb-3">
                                                                    <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                                                        <i className="simple-icon-layers" style={{ marginRight: '6px', fontSize: '12px', color: '#3498db' }}></i>
                                                                        * Tint Levels
                                                                    </Label>
                                                                    <Select
                                                                        placeholder="Select Tint Levels"
                                                                        isMulti
                                                                        name="levels"
                                                                        components={{ Input: CustomSelectInput }}
                                                                        options={service?.tintLevels?.sort((a, b) => Number(a.replace("%", '')) > Number(b.replace("%", '')) ? 1 : -1)?.map(option => { return !data?.darknessLevels?.includes(option) && { value: option, label: option, key: option } }).filter(rec => rec)}
                                                                        value={item?.darknessLevels?.sort((a, b) => Number(a.replace("%", '')) > Number(b.replace("%", '')) ? 1 : -1)?.map(rec => { return { value: rec, key: rec, label: rec } })}
                                                                        className="basic-multi-select"
                                                                        classNamePrefix="select"
                                                                        onChange={(val) => {
                                                                            let levels = val.map(rec => rec.value)
                                                                            let newVariation = JSON.parse(JSON.stringify(variation))
                                                                            let newData = JSON.parse(JSON.stringify(data))
                                                                            newData[itemIndex].darknessLevels = levels
                                                                            setData(newData)
                                                                            newVariation.data = newData
                                                                            setVariation(newVariation)
                                                                        }}
                                                                    />
                                                                </Colxx>
                                                                <Colxx xxs="12" className="mb-3">
                                                                    <Label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', marginBottom: '12px', display: 'block' }}>
                                                                        <i className="simple-icon-wallet" style={{ marginRight: '6px', fontSize: '12px', color: '#27ae60' }}></i>
                                                                        * Side Prices ({carType})
                                                                    </Label>
                                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                                                        {service?.tintingSides?.map(rec => (
                                                                            <div key={rec.key} style={{
                                                                                flex: '1 1 calc(25% - 12px)',
                                                                                minWidth: '200px',
                                                                                padding: '12px',
                                                                                border: '1px solid #e0e0e0',
                                                                                borderRadius: '8px',
                                                                                background: '#f8f9fa',
                                                                                transition: 'all 0.15s ease'
                                                                            }}
                                                                                onMouseEnter={(e) => {
                                                                                    e.currentTarget.style.borderColor = '#3498db';
                                                                                    e.currentTarget.style.background = '#e3f2fd';
                                                                                }}
                                                                                onMouseLeave={(e) => {
                                                                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                                                                    e.currentTarget.style.background = '#f8f9fa';
                                                                                }}
                                                                            >
                                                                                <div style={{
                                                                                    marginBottom: '8px',
                                                                                    fontSize: '13px',
                                                                                    fontWeight: '500',
                                                                                    color: '#2c3e50'
                                                                                }}>{rec.name}</div>
                                                                                <Input
                                                                                    className="customInput"
                                                                                    value={item?.[rec.key]?.[carKey] || ''}
                                                                                    placeholder="e.g. 10 AED"
                                                                                    type="number"
                                                                                    onChange={(e) => handleDateChange(e, itemIndex, rec.key)}
                                                                                />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </Colxx>
                                                            </>
                                                        )}
                                                    </Row>
                                                </CardBody>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '60px 20px',
                                        background: '#f8f9fa',
                                        borderRadius: '8px',
                                        border: '1px dashed #e0e0e0'
                                    }}>
                                        <i className="simple-icon-list" style={{
                                            fontSize: '48px',
                                            color: '#d0d0d0',
                                            marginBottom: '15px'
                                        }}></i>
                                        <p style={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#6c757d',
                                            margin: 0
                                        }}>No Options</p>
                                        <p style={{
                                            fontSize: '12px',
                                            color: '#999',
                                            margin: '5px 0 0 0'
                                        }}>Add variations to configure pricing</p>
                                    </div>
                                )}
                            </Colxx>
                        </Row>
                    </div>
                </ModalBody>
                <ModalFooter style={{
                    borderTop: '1px solid #e9ecef',
                    padding: '15px 25px',
                    background: '#f8f9fa'
                }}>
                    <Button
                        color="secondary"
                        outline
                        onClick={() => dismiss()}
                        style={{
                            borderRadius: '6px',
                            fontWeight: '500',
                            padding: '8px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <i className="simple-icon-close" style={{ fontSize: '12px' }}></i>
                        Cancel
                    </Button>
                    <Button
                        disabled={!active}
                        color="primary"
                        className="top-right-button"
                        onClick={() => {
                            UpdateVariation()
                        }}
                        style={{
                            borderRadius: '6px',
                            fontWeight: '500',
                            padding: '8px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        <i className="simple-icon-check" style={{ fontSize: '12px' }}></i>
                        {editVariation ? 'Save Variation' : 'Add Variation'}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default Variation;